<template>
  <div class="w-full flex flex-col justify-center overflow-hidden">
    <div class="lp-bg-gray flex w-full flex-col text-center items-center">
      <DialogManager />
      <router-view />
      <CookieConsentDialog v-if="showConsentDialog" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, onMounted, onUnmounted, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { IParcel, Parcel, Task, Note, INote, DialogMessageType, Wall, IWall, Sharing } from '@/common/classes'
  import { ParcelType, PermissionLevel, phoneBreakpoint, ScreensizeType, tabletBreakpoint } from '@/constants'

  import useWallStore from '@/composables/wallStore'
  import { useNoteStore } from '@/composables/noteStore'
  import { useUserStore } from '@/composables/userStore'

  import useDialogStore from '@/composables/dialog'
  import useParcelStore from '@/composables/parcel'
  import useFilterStore from '@/composables/filter'
  import useCommonStore from '@/composables/common'
  import useConsentStore from '@/composables/consent'

  import DialogManager from '@/components/dialog/DialogManager.vue'
  import CookieConsentDialog from '@/components/popups/CookieConsentDialog.vue'
  import { Group } from './common/interfaces'

  const showConsoleLogs = import.meta.env.VITE_SHOW_LOGS || false
  const router = useRouter()
  const route = useRoute()

  const parcelStore = useParcelStore()
  const wallStore = useWallStore()
  const noteStore = useNoteStore()
  const userStore = useUserStore()
  const dialogStore = useDialogStore()
  const common = useCommonStore()
  const filter = useFilterStore()
  const consent = useConsentStore()

  const { locale } = useI18n({ useScope: 'global' })

  const messages = {
    no: {
      wallshared1: 'delte tavla',
      wallshared2: 'med adgang til å ',
      wallshared3: 'med deg',
      walltemplate1: 'angitt at tavla kan kopieres',
      walltemplate2: 'av deg',
      walltemplate3: 'angitt at tavla ikke kan kopieres',
      wallpermission1: 'endret din adgang til tavla',
      wallremove1: 'fjernet din adgang til tavla',
      walldelete1: 'slettet tavla',
      walldelete2: 'slettet en tavle du har bidratt til',
      boardcreated1: 'En ny tavle er lagt til',
      boardcreated2: 'deltar på tavla',
      boardcreated3: 'Noen deltar på tavla',
      logoff: 'Tvungen utlogging',
      wallleave1: 'deltar ikke lengre på tavla',
      wallleave2: 'Noen deltar ikke lengre på tavla',
    },
    en: {
      wallshared1: 'shared the wall',
      wallshared2: 'with the permission',
      wallshared3: 'with you',
      walltemplate1: 'enabled the wall to be copied',
      walltemplate2: 'for you',
      walltemplate3: 'ddisabled the wall to be copied',
      wallpermission1: 'changed your permission on wall',
      wallremove1: 'removed your access rights to wall',
      walldelete1: 'deleted the wall',
      walldelete2: 'deleted a wall you are part of',
      boardcreated1: 'A new wall was created',
      boardcreated2: 'has joined the wall',
      boardcreated3: 'Someone has joined the wall',
      logoff: 'Forced logoff',
      wallleave1: 'has left the wall',
      wallleave2: 'Someone has left the wall',
    },
  }

  const { t } = useI18n({ messages })

  function getLabelForPermissionLevel(level?: PermissionLevel) {
    return level ? t(`${level}`) : ''
  }

  // Updates to our data initiated by other users are managed here
  const receiveParcel = (topic: string, payload: Buffer): void => {
    const parcelData: IParcel = JSON.parse(payload.toString())
    const parcel = new Parcel(parcelData)
    let title,
      wall,
      message = ''
    const user = userStore.getters.user.value
    if (!user) {
      router.push('/login')
    } else if (user._id !== parcel.subscription.user.id) {
      if (showConsoleLogs) console.log(`Incoming Parcel ${topic}: ${parcel._id}: ${parcel.parcelType}`)
      switch (parcel.parcelType) {
        // user topic
        case ParcelType.WallShared:
          message = `${parcel.subscription.user.username} ${t('wallshared1')} '${(parcel.body as Wall).details.title}' ${t(
            'wallshared2',
          )} '${getLabelForPermissionLevel(
            (parcel.body as Wall).users.shares.find((s: Sharing) => user.profile.groups.some((g: Group) => g.originalId === s.group_id))
              ?.permissionLevel,
          )}' ${t('wallshared3')}`
          dialogStore.actions.pushNotification(parcel.parcelType, DialogMessageType.Information, message)
          wallStore.actions.updateShares(new Wall(parcel.body as IWall))
          wallStore.actions.getUserWalls('') // get user walls after access rights might have changed
          wallStore.actions.getTemplateWalls()
          break

        case ParcelType.WallShareTemplate:
          message =
            (parcel.body as Wall).users.shares.find((s: Sharing) => user.profile.groups.some((g) => g.originalId === s.group_id))?.permissionLevel ===
            PermissionLevel.Copy
              ? `${parcel.subscription.user.username} ${t('walltemplate1')} '${(parcel.body as Wall).details.title}' ${t('walltemplate2')}`
              : `${parcel.subscription.user.username} ${t('walltemplate3')} '${(parcel.body as Wall).details.title}' ${t('walltemplate2')}`
          dialogStore.actions.pushNotification(parcel.parcelType, DialogMessageType.Information, message)
          wallStore.actions.updateShares(new Wall(parcel.body as IWall))
          wallStore.actions.getUserWalls('') // get user walls after access rights might have changed
          wallStore.actions.getTemplateWalls()
          break

        case ParcelType.WallPermission:
          message = `${parcel.subscription.user.username} ${t('wallpermission1')} '${(parcel.body as Wall).details.title}' ${t('to')} '${
            (parcel.body as Wall).users.shares.find((s: Sharing) => user.profile.groups.some((g) => g.originalId === s.group_id))?.permissionLevel
          }'`
          dialogStore.actions.pushNotification(parcel.parcelType, DialogMessageType.Information, message)
          wallStore.actions.updateShares(new Wall(parcel.body as IWall))
          wallStore.actions.getUserWalls('') // get user walls after access rights might have changed
          wallStore.actions.getTemplateWalls()
          break

        case ParcelType.WallShareRevoked:
          message = `${parcel.subscription.user.username} ${t('wallremove1')} '${(parcel.body as Wall).details.title}'`
          dialogStore.actions.pushNotification(parcel.parcelType, DialogMessageType.Information, message)
          wallStore.actions.updateShares(new Wall(parcel.body as IWall))
          wallStore.actions.getUserWalls('') // get user walls after access rights might have changed
          wallStore.actions.getTemplateWalls()
          break

        case ParcelType.WallEdited:
          wallStore.actions.updateWallDetails(new Wall(parcel.body as IWall))
          break

        case ParcelType.WallDeleted:
          wall = wallStore.getters.selectedWall.value
          if (wall) {
            if (wallStore.getters.selectedWall && parcel.subscription.wall_id === wall._id) {
              message = `${parcel.subscription.user.username} ${t('walldelete1')} ${wall.details.title}`
              dialogStore.actions.pushMessage(parcel.parcelType, DialogMessageType.Warning, message)
              router.push('/dashboard')
            } else {
              message = `${parcel.subscription.user.username} ${t('walldelete2')}`
              dialogStore.actions.pushNotification(parcel.parcelType, DialogMessageType.Warning, message)
            }
          }
          break

        case ParcelType.TaskCreate:
          wallStore.actions.updateTasks(parcel.body as Task[])
          break

        case ParcelType.TaskEdited:
          wallStore.actions.updateTasks(parcel.body as Task[])
          break

        case ParcelType.TaskDelete:
          wallStore.actions.updateTasks(parcel.body as Task[])
          break

        case ParcelType.MessageCreate:
          noteStore.actions.localCreate(new Note(parcel.body as INote))
          break

        case ParcelType.MessageEdited:
          noteStore.actions.localUpdate(new Note(parcel.body as INote))
          break

        case ParcelType.MessageBoard:
          noteStore.actions.localUpdate(new Note(parcel.body as INote))
          break

        case ParcelType.MessageDelete:
          noteStore.actions.localDelete(new Note(parcel.body as INote))
          break

        case ParcelType.BoardAdded: // this parcel is triggered when an anonymous user joins a wall (and creates a new board) or an admin creates a new board manually
          title = wallStore.actions.localBoardUpdate(new Wall(parcel.body as IWall)).name
          message =
            parcel.subscription.user.id === wallStore.getters.selectedWall.value?.users.owner.user_id
              ? t('boardcreated1')
              : parcel.subscription.user.isRegistered
                ? `${parcel.subscription.user.username} ${t('boardcreated2')}`
                : `${t('boardcreated3')} '${title}'`
          dialogStore.actions.pushMessage(title, DialogMessageType.Information, message, 2000)
          break

        case ParcelType.TaskMove:
          filter.actions.setTaskId(parcel.body as string)
          break

        case ParcelType.ModifyAccess:
          if (parcel.subscription.status.authorisedAccessOnly && !user.status.isRegistered) {
            const callback = () => {
              userStore.actions.logout()
            }
            dialogStore.actions.pushMessage('', DialogMessageType.Information, t('logoff'), 5000, callback)
            setTimeout(() => {
              router.push('/login')
            }, 500)
          }
          break
        case ParcelType.ModifyReadOnly:
          wallStore.actions.setReadOnlyStatus(parcel.subscription.status.readOnly)
          break
        case ParcelType.UserConnect:
          wall = wallStore.getters.selectedWall.value
          message = `${parcel.subscription.user.username} ${t('boardcreated2')}`
          dialogStore.actions.pushMessage(title, DialogMessageType.Information, message, 2000)
          break

        case ParcelType.UserContribution:
          wallStore.actions.updateContributionCount((parcel.body as string).split(','))
          break

        case ParcelType.UserDisconnect:
          if (window.location.href.includes('wall')) {
            message = parcel.subscription.user.isRegistered ? `${parcel.subscription.user.username} ${t('wallleave1')}` : t('wallleave2')
            dialogStore.actions.pushMessage(title, DialogMessageType.Information, message, 2000)
          }
          break

        case ParcelType.GlobalStatus:
        // TODO: Why was the page being reloaded here?
        // window.location.reload()

        default:
          break
      }
    }
  }

  onBeforeMount(() => {
    //window.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false })
    //window.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false })

    isMobileView()
  })

  onMounted(() => {
    window.addEventListener('resize', isMobileView)
    parcelStore.actions.setNewMessageCallback(receiveParcel)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', isMobileView)
  })

  router.beforeEach((to, from) => {
    const name = to.name?.toString().toLowerCase()
    if (name) {
      const wall = wallStore.getters.selectedWall.value
      const comingFromWall = !!from.name?.toString().toLowerCase().includes('wall')
      const goingToWall = name.includes('wall')
      if (comingFromWall && !goingToWall && wall) {
        const id = wall._id
        // route navigation - send the user disconnect parcel once another URL was called
        const parcel = new Parcel({ parcelType: ParcelType.UserDisconnect, body: id }, userStore.getters.user.value, wall)
        parcelStore.actions.sendParcel(id, parcel).then(() => parcelStore.actions.unSubscribeTopic(id))
        const user = userStore.getters.user.value
        if (user) {
          for (const group of user.profile.groups) {
            parcelStore.actions.subscribeTopic(group.originalId)
          }
        }
      }
    }
  })

  // Ask for consent when entering the app; the privacy page can reopen it to change the choice
  const showConsentDialog = computed(
    () => consent.getters.dialogIsOpen.value || (!consent.getters.hasChosen.value && ['Dashboard', 'Wall'].includes(route.name?.toString() ?? '')),
  )

  watch(common.getters.languageCode, (language) => {
    locale.value = language // change i18n locale
    document.documentElement.lang = language.includes('no') ? 'nb' : 'en'
  })

  // Norwegian is the default language, regardless of browser settings
  common.actions.setLanguageCodeByValue('no')

  function isMobileView() {
    const userAgent = navigator.userAgent
    const isPhone = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Phone/i.test(userAgent)
    const isTablet = /iPad|Tab/i.test(userAgent)
    if (isPhone) common.actions.setScreensizeType(ScreensizeType.phone)
    else if (isTablet) common.actions.setScreensizeType(ScreensizeType.tablet)
    else common.actions.setScreensizeType(ScreensizeType.desktop)
  }
</script>

<style></style>
